import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertOctagon, RefreshCw, Home } from 'lucide-react'
import { isChunkError } from '@/lib/chunkReload'

/**
 * Error boundary global de la app.
 *
 * React no expone errores síncronos de render en hooks de función; hay
 * que envolver el árbol con un componente de clase que implemente
 * `componentDidCatch` / `getDerivedStateFromError`. Cualquier crash
 * (chunk lazy que falla, librería que tira, render condicional con
 * `undefined.foo`) cae aquí en vez de dejar la pantalla en blanco.
 *
 * Comportamiento:
 *
 *   1. Captura el error y muestra una pantalla amable.
 *   2. Si `window.Sentry` existe (cuando se añada Sentry env-gated),
 *      reporta el error con `Sentry.captureException`.
 *   3. Ofrece dos acciones: recargar la página (descarta el estado en
 *      memoria pero conserva localStorage) y volver al inicio.
 *   4. En desarrollo muestra el stack trace; en producción solo el
 *      mensaje + un identificador para correlacionar con Sentry si
 *      llega.
 *
 * Reutilizable: se monta en `App.tsx` envolviendo todo el árbol del
 * router. No hace asunciones sobre el dominio del error.
 */

interface State {
  error: Error | null
  errorInfo: ErrorInfo | null
}

declare global {
  interface Window {
    Sentry?: {
      captureException?: (err: unknown, ctx?: unknown) => void
    }
  }
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, errorInfo: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })

    // Recarga si el fallo es de un trozo de codigo que ya no existe
    // tras un despliegue. La deteccion vive en lib/chunkReload para
    // no repetir la lista de mensajes en dos sitios.
    if (isChunkError(error.message)) {
      const reloadKey = 'mw-chunk-reload'
      const lastReload = sessionStorage.getItem(reloadKey)
      const now = Date.now()
      // Only auto-reload once per 30 seconds to avoid loops
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        sessionStorage.setItem(reloadKey, String(now))
        window.location.reload()
        return
      }
    }

    // Reporta a Sentry si está disponible
    if (typeof window !== 'undefined' && window.Sentry?.captureException) {
      try {
        window.Sentry.captureException(error, { contexts: { react: errorInfo } })
      } catch {
        /* no rompemos por un fallo del reporter */
      }
    } else {
      console.error('[ErrorBoundary]', error, errorInfo)
    }
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null })
  }

  reload = () => {
    window.location.reload()
  }

  goHome = () => {
    window.location.href = '/'
  }

  render() {
    if (!this.state.error) return this.props.children

    const message = this.state.error.message || 'Error desconocido'

    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'white',
          fontFamily: "var(--mw-font-body, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif)",
        }}
      >
        <div style={{ maxWidth: 440, width: '100%', textAlign: 'center' }}>
          <div
            style={{
              width: 56,
              height: 56,
              margin: '0 auto 16px',
              borderRadius: '50%',
              background: '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AlertOctagon size={28} color="#DC2626" strokeWidth={1.75} aria-hidden />
          </div>

          <h1
            style={{
              fontFamily: "var(--mw-font-display, -apple-system, sans-serif)",
              fontSize: 22,
              fontWeight: 600,
              color: '#131830',
              margin: '0 0 8px',
              letterSpacing: '-0.02em',
            }}
          >
            Algo ha ido mal
          </h1>
          <p style={{ fontSize: 14, color: '#6B7280', margin: '0 0 24px', lineHeight: 1.5 }}>
            La página no se ha podido cargar. Recarga para volver a intentarlo.
          </p>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={this.reload}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                height: 42,
                padding: '0 20px',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                background: '#2563EB',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <RefreshCw size={15} strokeWidth={2} aria-hidden />
              Recargar
            </button>
            <button
              type="button"
              onClick={this.goHome}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                height: 42,
                padding: '0 20px',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                background: 'white',
                color: '#131830',
                border: '1px solid #E5E7EB',
                cursor: 'pointer',
              }}
            >
              <Home size={15} strokeWidth={2} aria-hidden />
              Inicio
            </button>
          </div>

          <details style={{ marginTop: 32, textAlign: 'left' }}>
            <summary style={{ fontSize: 12, color: '#9CA3AF', cursor: 'pointer', marginBottom: 8 }}>
              Detalles técnicos
            </summary>
            <pre
              style={{
                fontSize: 11,
                fontFamily: 'monospace',
                color: '#DC2626',
                background: '#F9FAFB',
                padding: 12,
                borderRadius: 8,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                margin: 0,
                maxHeight: 200,
              }}
            >
              {message}
              {this.state.error.stack ? '\n\n' + this.state.error.stack : ''}
              {this.state.errorInfo?.componentStack
                ? '\n\nComponent stack:' + this.state.errorInfo.componentStack
                : ''}
            </pre>
          </details>
        </div>
      </div>
    )
  }
}
